/* ---------- time: one parser for every feature (DOM-free) ---------- */

import { memo } from './memo';

const US_ZONES: Record<string, string> = {
  P: 'America/Los_Angeles',
  M: 'America/Denver',
  C: 'America/Chicago',
  E: 'America/New_York',
  AK: 'America/Anchorage',
  HI: 'Pacific/Honolulu',
};
// "2026-05-03", "2026-5-3 7:30 pm", "2026-05-03T19:30:00" — am/pm → 12h, otherwise 24h, no time → midnight
const DATE_RE = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:(?:\s+|T)(\d{1,2})(?::(\d{2}))?(?::(\d{2}))?\s*(am|pm)?)?$/i;

// "GMT+3", "UTC -5", "gmt+09:00" — whole hours only; the Etc/GMT zones are inverted (Etc/GMT-3 = UTC+3) and span -14..+12
const GMT_RE = /^(?:GMT|UTC)\s*([+-])\s*(\d{1,2})(?::?00)?$/i;

/** "PT" | "EDT" | "AK" | "GMT+3" … → IANA; anything unrecognised is passed through as IANA. */
export const toIana = (key: string) => {
  const gmt = key.match(GMT_RE);
  if (gmt) {
    const hours = +(gmt[2] ?? 0) * (gmt[1] === '-' ? -1 : 1);
    if (hours === 0) return 'UTC';
    if (hours >= -12 && hours <= 14) return `Etc/GMT${hours > 0 ? '-' : '+'}${Math.abs(hours)}`;
  }
  return US_ZONES[key.toUpperCase().replace(/^([PMCE])[SD]?T$/, '$1')] ?? key;
};

/** Whether Intl knows `timeZone` — a bad name throws RangeError at construction, so the check happens once per value. */
export const isZone = memo((timeZone: string) => {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
});

const zoneFormatter = memo(
  (timeZone: string) =>
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23', // hour12:false can yield "24", and hour12 silently overrides hourCycle
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }),
);

/** Offset (ms) between wall-clock time in `timeZone` and UTC at the given instant. */
export const zoneOffset = (utcMs: number, timeZone: string) => {
  const parts = zoneFormatter(timeZone).formatToParts(new Date(utcMs));
  const z: Partial<Record<Intl.DateTimeFormatPartTypes, number>> = Object.fromEntries(
    parts.map(({ type, value }) => [type, +value]),
  );
  const { year = NaN, month = NaN, day = NaN, hour = NaN, minute = NaN, second = NaN } = z;
  return Date.UTC(year, month - 1, day, hour, minute, second) - utcMs;
};

type WallClock = [y: number, m0: number, d: number, h: number, min: number, s: number];

/** Wall-clock parts from DATE_RE, or from anything the browser can read (Webflow's "June 9, 2026 6:00 PM"). */
const wallClock = (raw: string): WallClock | null => {
  const m = raw.match(DATE_RE);
  if (m) {
    const [, y = '', mo = '', d = '', h = '0', min = '0', s = '0', ampm] = m;
    return [+y, +mo - 1, +d, ampm ? (+h % 12) + (/pm/i.test(ampm) ? 12 : 0) : +h, +min, +s];
  }
  const d = new Date(raw);
  return Number.isNaN(d.getTime())
    ? null
    : [d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds()];
};
const HAS_OFFSET = /(Z|[+-]\d{2}:?\d{2})$/i; // "…T18:00:00Z" / "…-07:00" already name an instant — no zone shifting

/** Reads a wall-clock string in `timeZone` (null = browser-local) and returns the real instant, DST-safe. */
export const parseZoned = (raw: string | null | undefined, timeZone: string | null): Date | null => {
  const text = (raw ?? '').trim();
  if (!text) return null;
  if (HAS_OFFSET.test(text)) {
    const d = new Date(text);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const parts = wallClock(text);
  if (!parts) return null;
  if (!timeZone) return new Date(...parts);
  const guess = Date.UTC(...parts);
  return new Date(guess - zoneOffset(guess - zoneOffset(guess, timeZone), timeZone)); // 2nd pass settles DST edges
};

const DAY_MS = 86400e3;
/** Calendar-day number (days since epoch) of an instant as seen on the wall clock in `timeZone` (null = browser-local). */
export const calendarDay = (utcMs: number, timeZone: string | null) =>
  Math.floor((utcMs + (timeZone ? zoneOffset(utcMs, timeZone) : -new Date(utcMs).getTimezoneOffset() * 60e3)) / DAY_MS);

export const formatZoned = (date: Date | null, timeZone: string | null) =>
  date
    ? new Intl.DateTimeFormat('en-US', {
        timeZone: timeZone || undefined,
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }).format(date)
    : '';
